import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { SlackIcon } from "./slackIcon";
import { parseSlackMessagesPayload, type SlackMessagesPayload } from "./slackMessagesSchema";
import { formatSlackTs, SlackMessageText } from "./slackRenderUtils";

interface SlackMessagesRendererProps {
  block: StreamingBlock;
}

// Permalinks arrive from tool output and are untrusted; only https URLs are
// safe to put in an href.
const safeSlackPermalink = (url: string | undefined): string | undefined =>
  url?.startsWith("https://") ? url : undefined;

export const SlackMessagesRenderer = (props: SlackMessagesRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<SlackMessagesPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseSlackMessagesPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("slack_messages_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().messages.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <SlackIcon class="size-4 shrink-0" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {data().threadTs
                    ? rt.t("slack_messages_card.thread_title", { channel: `#${data().channelName}` })
                    : `#${data().channelName}`}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().messages.length === 1
                    ? rt.t("slack_messages_card.results_one")
                    : rt.t("slack_messages_card.results", { count: String(data().messages.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().messages}>
                  {(message) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="truncate text-sm font-medium text-primary-text">{message.author}</span>
                          <Show when={message.isBot}>
                            <span class="shrink-0 rounded bg-inset px-1 text-[10px] text-muted-text uppercase">
                              {rt.t("slack_messages_card.bot_badge")}
                            </span>
                          </Show>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatSlackTs(message.ts)}</span>
                      </div>
                      <p class="mt-1 line-clamp-3 text-sm whitespace-pre-wrap text-primary-text">
                        <SlackMessageText text={message.text} mentions={data().mentions} />
                      </p>
                      <Show when={safeSlackPermalink(message.permalink)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("slack_messages_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
